import React, { useEffect, useState } from "react";
import { WifiOff, RefreshCw, AlertTriangle, CloudDownload } from "lucide-react";
import { getQueue, flushQueue, nietVerwerktLijst, nietVerwerktOpnieuw, nietVerwerktVerwijder, pakketLezen, pakketVerversen } from "@/lib/offline";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function NietVerwerkt({ items, onSluit }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onSluit()}>
      <DialogContent className="max-w-lg" data-testid="niet-verwerkt-dialoog">
        <DialogHeader><DialogTitle>Niet verwerkt ({items.length})</DialogTitle><DialogDescription className="text-xs">Deze offline ingevulde formulieren zijn door de server geweigerd. Niets is weggegooid: pas aan en verstuur opnieuw, of verwijder bewust.</DialogDescription></DialogHeader>
        <div className="space-y-2 text-xs max-h-[60vh] overflow-auto">
          {items.map((it) => (
            <div key={it.nv_id} className="border rounded p-2" data-testid={`niet-verwerkt-${it.nv_id}`}>
              <div className="font-medium">{it.label || it.path}</div>
              <div className="text-rose-700">{it.reden}</div>
              <div className="text-slate-400">{it.created_at?.slice(0, 16).replace("T", " ")} → geweigerd {it.geweigerd_op?.slice(0, 16).replace("T", " ")}</div>
              <details><summary className="cursor-pointer">gegevens</summary><pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(it.payload, null, 1)}</pre></details>
              <div className="flex gap-1 mt-1"><Button size="sm" variant="outline" className="h-7" onClick={() => nietVerwerktOpnieuw(it.nv_id)} data-testid={`niet-verwerkt-opnieuw-${it.nv_id}`}>Opnieuw versturen</Button><Button size="sm" variant="ghost" className="h-7 text-rose-700" onClick={() => { if (window.confirm("Definitief verwijderen?")) nietVerwerktVerwijder(it.nv_id); }} data-testid={`niet-verwerkt-verwijder-${it.nv_id}`}>Verwijderen</Button></div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OfflineSync() {
  const [online, setOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(0);
  const [nv, setNv] = useState([]);
  const [pakket, setPakket] = useState(pakketLezen());
  const [open, setOpen] = useState(false);

  const refresh = () => { getQueue().then((q) => setCount(q.length)).catch(() => {}); setNv(nietVerwerktLijst()); };

  useEffect(() => {
    refresh();
    const on = () => { setOnline(true); flushQueue().then(refresh); pakketVerversen().then((p) => p && setPakket(p)); };
    const off = () => setOnline(false);
    const pk = () => setPakket(pakketLezen());
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("bpt-offline-queue", refresh);
    window.addEventListener("bpt-offline-pakket", pk);
    if (navigator.onLine) { flushQueue().then(refresh); setTimeout(() => pakketVerversen().then((p) => p && setPakket(p)), 4000); }
    const t = setInterval(() => navigator.onLine && pakketVerversen(), 30 * 60 * 1000);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("bpt-offline-queue", refresh);
      window.removeEventListener("bpt-offline-pakket", pk);
      clearInterval(t);
    };
  }, []);

  const flagAan = !!window.__bptFlags?.mobiel_offline_v1;
  if (online && count === 0 && nv.length === 0) return null;
  return (
    <>
      <div className="fixed bottom-4 left-4 z-[100] flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-xs font-medium bg-slate-900 text-white" data-testid="offline-sync-chip">
        {!online ? (
          <><WifiOff className="h-3.5 w-3.5 text-amber-400" /> Offline{count > 0 ? ` — ${count} formulier(en) in wachtrij` : " — wijzigingen worden bewaard"}{flagAan && pakket && <span className="text-slate-300 flex items-center gap-1" data-testid="offline-pakket-stand"><CloudDownload className="h-3 w-3" />{pakket.werkbonnen.length} werkbonnen tot {pakket.op.slice(0, 16).replace("T", " ")}</span>}</>
        ) : count > 0 ? (
          <><RefreshCw className="h-3.5 w-3.5 animate-spin text-emerald-400" /> {count} formulier(en) synchroniseren…</>
        ) : null}
        {nv.length > 0 && <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-amber-300 underline" data-testid="niet-verwerkt-knop"><AlertTriangle className="h-3.5 w-3.5" />{nv.length} niet verwerkt</button>}
      </div>
      {open && <NietVerwerkt items={nv} onSluit={() => setOpen(false)} />}
    </>
  );
}
